import type { ToastsRenderer } from '@iheartjane/reefer';
import { clsx } from 'clsx';
import { useEffect, useMemo } from 'react';
import type { UIMatch } from 'react-router';
import { useMatches } from 'react-router';

import { Toast } from '@jane/bloom/components/toast/toast';
import { useHasStoreBanner } from '@jane/bloom/hooks/useHasStoreBanner';
import type { LoaderResponse } from '@jane/bloom/routes/$basename.products.$productId.$slug/types';
import { accountPages } from '@jane/bloom/utils/routes/routeIds';

import styles from './toast.module.css';

export function Toasts({ remove, toasts }: ToastsRenderer) {
  const matches = useMatches();
  const accountPage = matches.find(({ id }) => accountPages.includes(id));
  const pdpPage = useMemo(
    () =>
      matches.find(
        ({ id }) => id === 'routes/$basename.products.$productId.$slug'
      ) as UIMatch<Partial<LoaderResponse>>,
    [matches]
  );
  const weightedPdpPage =
    pdpPage && pdpPage.data?.productDetail?.hasMultipleWeights;
  const unweightedPdpPage =
    pdpPage && !pdpPage.data?.productDetail?.hasMultipleWeights;

  // Mobile Safari doesn't correctly recompute the height of the screen after redirecting (even when using the `dvh` unit)
  // It results in the toast being potentially hidden behind the address bar
  // If Safari browser is detected, scroll by one px which triggers the screen to resize correctly and show the toast
  useEffect(() => {
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

    if (isSafari && toasts.length) {
      window.scrollBy(0, 1);
    }
  }, [toasts.length]);

  // Scroll to the top when a toast appears in account
  useEffect(() => {
    if (accountPage && toasts.length && toasts.length > 0) {
      window.scrollTo(0, 0);
    }
  }, [accountPage, toasts.length]);

  const { hasStoreBanner } = useHasStoreBanner();

  return (
    <div
      className={clsx(styles['wrapper'], {
        [styles['wrapper--account']]: accountPage,
        [styles['wrapper--locationStoreBanner']]: hasStoreBanner,
        [styles['wrapper--unweightedPdp']]: unweightedPdpPage,
        [styles['wrapper--weightedPdp']]: weightedPdpPage,
      })}
    >
      {toasts.map((toast) => (
        <Toast key={toast.id} onClose={remove} {...toast} />
      ))}
    </div>
  );
}
